import React, { useEffect, useInsertionEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ConfirmationModal from '../components/ConfirmationModal';
import LoadingIndicatorModal from '../components/LoadingIndicatorModal';
import { gql } from '../graphql/generated/gql';
import { useAddTopicToPost } from '../post/sharing/postPermissionOperations';
import normalizeGraphQLError, {
  ErrorCodes,
} from '../utils/error/normalizeGraphQLError';
import useScript from '../utils/network/useScript';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';

const GOOGLE_API_URL = 'https://apis.google.com/js/api.js';

const GOOGLE_IMPORT_MUTATION = gql(/** @complexity 2 */ `
  mutation googleImport($externalId: String!) {
    importExternal(platform: "google-drive", externalId: $externalId) {
      ...postPath_post
    }
  }
`);

// eslint-disable-next-line import/no-unused-modules -- expose for storybook
export const IMPORT_ERRORS = {
  INVALID_FORMAT: 'The file format is not supported.',
  UNKNOWN: 'We are sorry, but the import failed for unknown reason.',
  DOCUMENT_IMPORT_FAILED: 'Document import failed.',
} as const;

type ImportError = keyof typeof IMPORT_ERRORS;

// eslint-disable-next-line import/no-unused-modules -- expose for storybook
export const ImportErrorModal = ({
  errorCode,
  close,
  onTryAgain,
}: {
  errorCode: ImportError;
  close: () => void;
  onTryAgain: () => void;
}) => (
  <ConfirmationModal
    title="Import error"
    description={IMPORT_ERRORS[errorCode]}
    close={close}
    cancelButtonText="Close"
    confirmButton={{
      onClick: onTryAgain,
      text: 'Try again',
    }}
  />
);

interface GoogleDrivePickerProps {
  close: () => void;
  navigationMethod?: 'push' | 'replace';
  topicIds?: string[];
  onBeforeNavigate?: () => Promise<void>;
}

interface GoogleDrivePickerAuthenticatedProps extends GoogleDrivePickerProps {
  accessToken: string;
  publishableKey: string;
  setError: (error: ImportError) => void;
}

const GoogleDrivePickerAuthenticated = ({
  close,
  navigationMethod = 'push',
  topicIds,
  accessToken,
  publishableKey,
  setError,
  onBeforeNavigate,
}: GoogleDrivePickerAuthenticatedProps) => {
  const navigate = useNavigate();

  const [importExternal, { loading }] = useMutation(GOOGLE_IMPORT_MUTATION);

  const addTopic = useAddTopicToPost();

  const onResultRef = useRef<(result: google.picker.ResponseObject) => void>(
    () => {},
  );

  useEffect(() => {
    const handleImport = async (externalId: string) => {
      try {
        const response = await importExternal({ variables: { externalId } });

        const post = response.data?.importExternal;
        if (post) {
          close();
          if (Array.isArray(topicIds)) {
            await Promise.all(
              topicIds.map((topicId) => addTopic(post.id, topicId)),
            );
          }
          if (onBeforeNavigate) {
            await onBeforeNavigate();
          }
          navigate(postPath(post), { replace: navigationMethod === 'replace' });
        }
      } catch (e) {
        setError(
          normalizeGraphQLError(e).code === ErrorCodes.INVALID_FORMAT
            ? 'INVALID_FORMAT'
            : 'UNKNOWN',
        );
      }
    };
    onResultRef.current = (result: google.picker.ResponseObject) => {
      switch (result.action) {
        case google.picker.Action.CANCEL: {
          close();
          break;
        }
        case google.picker.Action.PICKED: {
          const externalId = result.docs[0]?.id;
          if (!externalId) {
            setError('DOCUMENT_IMPORT_FAILED');
            break;
          }
          handleImport(externalId);
          break;
        }
        default: {
          break;
        }
      }
    };
  }, [
    addTopic,
    close,
    importExternal,
    navigationMethod,
    navigate,
    setError,
    topicIds,
    onBeforeNavigate,
  ]);

  useEffect(() => {
    let docPicker: google.picker.Picker | undefined;
    window.gapi?.load('picker', () => {
      docPicker = new google.picker.PickerBuilder()
        .setDeveloperKey(publishableKey)
        .setOAuthToken(accessToken)
        .addView(new google.picker.DocsView(google.picker.ViewId.DOCUMENTS))
        .setCallback(onResultRef.current)
        .build();
      docPicker.setVisible(true);
    });

    return () => {
      docPicker?.dispose();
    };
  }, [accessToken, publishableKey]);

  useInsertionEffect(() => {
    const styleTag = document.createElement('style');
    document.head.appendChild(styleTag);

    styleTag.textContent = `
      .picker.picker-dialog-bg {
        background: none;
      }
    `;

    return () => {
      styleTag.remove();
    };
  }, []);

  if (loading) return <LoadingIndicatorModal />;

  return null;
};

const GOOGLE_DRIVE_PICKER_QUERY = gql(/** @complexity 1 */ `
  query GoogleDrivePicker($redirectPath: String!) {
    googleDrivePickerAccessToken(redirectPath: $redirectPath) {
      authorizationUrl
      publishableKey
      token
    }
  }
`);

const getRedirectPath = (topicIds: string[] = []) =>
  `?${new URLSearchParams([
    ['importFrom', 'google'],
    ...topicIds.map((topicId) => ['topicIds', topicId]),
  ])}`;

const GoogleDrivePicker = (props: GoogleDrivePickerProps) => {
  const { close } = props;
  const [errorCode, setErrorCode] = useState<ImportError | null>(null);

  const { data, loading, refetch } = useQuery(GOOGLE_DRIVE_PICKER_QUERY, {
    variables: { redirectPath: getRedirectPath(props.topicIds) },
  });
  const googleDrivePickerAccessToken = data?.googleDrivePickerAccessToken;

  const { hasLoaded } = useScript(GOOGLE_API_URL);

  if (loading || !hasLoaded) {
    return <LoadingIndicatorModal />;
  }

  if (!googleDrivePickerAccessToken) {
    return (
      <ConfirmationModal
        title="Problem loading"
        description="There is some issue loading Google Drive Picker. Please try again."
        close={close}
        cancelButtonText="Later"
        confirmButton={{
          onClick: () => refetch(),
          text: 'Try again',
        }}
      />
    );
  }

  const { token, publishableKey, authorizationUrl } =
    googleDrivePickerAccessToken;

  if (!token) {
    return (
      <ConfirmationModal
        title="Import from Google"
        description="To continue please sign in with Google account to import your files."
        close={close}
        cancelButtonText="Close"
        confirmButton={{
          to: authorizationUrl,
          text: 'Sign in with Google',
        }}
      />
    );
  }

  if (errorCode) {
    return (
      <ImportErrorModal
        errorCode={errorCode}
        close={close}
        onTryAgain={() => setErrorCode(null)}
      />
    );
  }

  return (
    <GoogleDrivePickerAuthenticated
      {...props}
      accessToken={token}
      publishableKey={publishableKey}
      setError={setErrorCode}
    />
  );
};

export default GoogleDrivePicker;
